import type { Components } from 'react-markdown';
import {
  renderContentScannerAnchor,
  renderContentScannerInlineCode,
} from './content-scanner-dispatch';
import { ProseCodeBlock, ProseInlineCode } from './code-blocks';
import { MermaidBlock } from '../MermaidBlock';
import { forwardedHtmlProps, listStyleClass } from './html-attributes';
import { isBlockCode, wrapsCodeElement } from './is-block-code';
import { sourceLineProps } from './source-lines';

// Call this through `buildComponents` in `component-maps.ts`, never directly:
// the returned map's identity is load-bearing for react-markdown.
//
// Every override spreads `forwardedHtmlProps` so the attributes the sanitizer
// allowed (`align`, `colspan`, `id`, `title`, narrowed `style`) survive the
// swap to a styled element instead of being dropped here.
export function createProseComponents(): Components {
  return {
    h1: (props) => (
      <h1
        {...forwardedHtmlProps(props)}
        className="mt-0 mb-3 text-4xl md:text-[2.75rem] font-bold tracking-tight leading-[1.1] scroll-mt-24"
      >
        {props.children}
      </h1>
    ),
    h2: (props) => (
      <h2
        {...forwardedHtmlProps(props)}
        className="mt-14 mb-5 pb-3 border-b border-border text-2xl md:text-[1.75rem] font-semibold tracking-tight leading-tight scroll-mt-24"
      >
        {props.children}
      </h2>
    ),
    h3: (props) => (
      <h3
        {...forwardedHtmlProps(props)}
        className="mt-10 mb-3 text-xl font-semibold tracking-tight leading-snug scroll-mt-24"
      >
        {props.children}
      </h3>
    ),
    h4: (props) => (
      <h4
        {...forwardedHtmlProps(props)}
        className="mt-8 mb-2 text-base font-semibold tracking-tight leading-snug scroll-mt-24"
      >
        {props.children}
      </h4>
    ),
    h5: (props) => (
      <h5 {...forwardedHtmlProps(props)} className="mt-6 mb-2 text-sm font-semibold scroll-mt-24">
        {props.children}
      </h5>
    ),
    h6: (props) => (
      <h6
        {...forwardedHtmlProps(props)}
        className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground scroll-mt-24"
      >
        {props.children}
      </h6>
    ),
    p: (props) => (
      <p
        {...forwardedHtmlProps(props)}
        className="my-5 text-base leading-[1.75] text-foreground/85"
      >
        {props.children}
      </p>
    ),
    ul: (props) => (
      <ul
        {...forwardedHtmlProps(props)}
        className={`my-5 ml-6 ${listStyleClass(props, 'list-disc')} space-y-2.5 text-base leading-[1.75] marker:text-muted-foreground/50`}
      >
        {props.children}
      </ul>
    ),
    ol: (props) => (
      <ol
        {...forwardedHtmlProps(props)}
        className={`my-5 ml-6 ${listStyleClass(props, 'list-decimal')} space-y-2.5 text-base leading-[1.75] marker:text-muted-foreground/60`}
      >
        {props.children}
      </ol>
    ),
    li: (props) => (
      <li
        {...forwardedHtmlProps(props)}
        className="pl-1 leading-[1.75] text-foreground/85 [&>p]:my-1.5 [&>ul]:my-2 [&>ol]:my-2"
      >
        {props.children}
      </li>
    ),
    strong: (props) => (
      <strong {...forwardedHtmlProps(props)} className="font-semibold text-foreground">
        {props.children}
      </strong>
    ),
    em: (props) => (
      <em {...forwardedHtmlProps(props)} className="italic text-foreground/90">
        {props.children}
      </em>
    ),
    a: ({ children, href }) =>
      renderContentScannerAnchor(href, children, () => (
        <a
          href={href}
          className="font-medium text-primary underline decoration-primary/40 underline-offset-[3px] transition-colors hover:decoration-primary"
          {...(href && /^https?:/i.test(href)
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          {children}
        </a>
      )),
    blockquote: (props) => (
      <blockquote
        {...forwardedHtmlProps(props)}
        className="my-6 border-l-2 border-border bg-muted/20 px-5 py-3 text-foreground/75 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
      >
        {props.children}
      </blockquote>
    ),
    hr: (props) => <hr {...forwardedHtmlProps(props)} className="my-12 border-border/60" />,
    table: (props) => (
      <div className="my-6 overflow-x-auto rounded-lg border bg-muted/30">
        <table {...forwardedHtmlProps(props)} className="w-full border-collapse text-[14px]">
          {props.children}
        </table>
      </div>
    ),
    caption: (props) => (
      <caption
        {...forwardedHtmlProps(props)}
        className="px-4 py-2 text-left text-xs text-muted-foreground"
      >
        {props.children}
      </caption>
    ),
    thead: (props) => (
      <thead {...forwardedHtmlProps(props)} className="border-b border-border bg-muted/30">
        {props.children}
      </thead>
    ),
    tbody: (props) => (
      <tbody
        {...forwardedHtmlProps(props)}
        className="[&>tr:nth-child(even)]:bg-muted/15 [&>tr:hover]:bg-muted/30"
      >
        {props.children}
      </tbody>
    ),
    tr: (props) => (
      <tr
        {...forwardedHtmlProps(props)}
        className="border-b border-border/60 last:border-b-0 transition-colors"
      >
        {props.children}
      </tr>
    ),
    th: (props) => (
      <th
        {...forwardedHtmlProps(props)}
        className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {props.children}
      </th>
    ),
    td: (props) => (
      <td {...forwardedHtmlProps(props)} className="px-4 py-3 align-top text-foreground/85">
        {props.children}
      </td>
    ),
    code: ({ children, className }) => {
      const text = String(children).replace(/\n$/, '');
      if (isBlockCode(className, text)) {
        const lang = className?.replace('language-', '') ?? '';
        if (lang === 'mermaid') {
          return <MermaidBlock code={text} size="prose" />;
        }
        return <ProseCodeBlock code={text} lang={lang || undefined} />;
      }
      return renderContentScannerInlineCode(text, (content) => (
        <ProseInlineCode>{content}</ProseInlineCode>
      ));
    },
    pre: (props) => {
      if (wrapsCodeElement(props.node)) {
        // The `code` override renders the whole block shell, so this `pre`
        // normally collapses to a fragment. Under source-line tracking it has
        // to leave *some* element behind, or a fenced block would be the one
        // thing in a document you cannot point back at a line. A bare wrapper
        // div is layout-neutral: the shell's own margins collapse through it.
        const sourceLine = sourceLineProps(props);
        return Object.keys(sourceLine).length > 0 ? (
          <div {...sourceLine}>{props.children}</div>
        ) : (
          <>{props.children}</>
        );
      }
      return (
        <pre
          {...forwardedHtmlProps(props)}
          className="my-5 overflow-x-auto rounded-lg border bg-muted/30 p-4 font-mono text-[13px] leading-relaxed"
        >
          {props.children}
        </pre>
      );
    },
  };
}
